// Source of truth for the runtime feature flags evaluated through ConfigCat
// (ADR-045). Deliberately shared between the browser and the Lambdas: the two
// halves must agree on which keys exist and what each falls back to, and a copy
// on each side kept honest by a parity guard is a shape this repo has already
// paid for once — see CLAUDE.md on the `{{ payload.x }}` token grammar, where
// "a parity test catches divergence but not duplication".
//
// This file has NO runtime dependencies, and must keep none. It is imported by
// the browser bundle, so anything reachable from here ships to every visitor.
// The ConfigCat SDK lives behind ./node (Lambdas) and in 3sb-app's own
// featureFlags/client.ts (browser).
//
// TO ADD A FLAG
//   1. Add an entry below.
//   2. Create a setting with the SAME key in BOTH ConfigCat environments.
//      A key that exists in dev and not in prod resolves to the default in
//      prod, silently.
//   3. See docs/architecture/feature-flags.md for the backend wiring, which is
//      per-module and is not automatic.
//
// A KEY IS IMMUTABLE ONCE SHIPPED. Dashboard targeting rules reference the key,
// so renaming one detaches every rule written against it — with no error, since
// an unknown key is simply absent and absent means "use the default".
//
// `default` IS THE DEPLOYED BEHAVIOUR, NOT "OFF". A release flag defaults false.
// A kill switch defaults TRUE, because an unreachable ConfigCat must not disable
// the very thing the switch exists to guard.
export const FEATURE_FLAGS = {
    LLMIntegration: {
        default: false,
        description: "Hosted Claude and ChatGPT connector consent, OAuth issuance and delegated API access. Disabled by default; existing integrations and access-control enforcement remain active.",
    },
    // Release flag for the Donorfy importer's module claims (a module or
    // integration claiming Donorfy activity and connection types).
    "donorfy-module-claims": {
        default: false,
        description: "Lets a Donorfy migration hand activity types and connection types to a module or " +
            "integration (Case Management, Sponsorship, Mailchimp, ...) instead of importing them " +
            "as plain touchpoints and connections. Off hides claims entirely.",
    },
    // A UX experiment between two layouts of the same feature; both write the
    // same claims, so flipping it never loses one. Read only while
    // donorfy-module-claims is on.
    "donorfy-module-claims-step": {
        default: false,
        description: "With donorfy-module-claims on: true shows claims as their own wizard step, organised " +
            "by module; false puts a 'Belongs to' column in the Lookups step's activity-type and " +
            "connection-type mappers.",
    },
    // Release flag for live updates (ADR-068): pages subscribe to AppSync Events
    // and refetch on a hint instead of polling every few seconds. Off keeps the
    // polling exactly as it was; on, pages still poll every 60 seconds as a
    // safety net, and fall back to fast polling whenever the socket is down.
    "realtime-updates": {
        default: false,
        description: "Pages that support it (the Importer's job and sync screens first) refresh when the " +
            "server says something changed, over a WebSocket, and poll slowly as a safety net. " +
            "Off keeps the existing fast polling.",
    },
    "example-inert-flag": {
        default: false,
        description: "No behaviour attached. Proves the ConfigCat wiring resolves end to end: " +
            "flipping it changes one field on one structured log line and nothing else.",
    },
};
// The cast is sound only because FEATURE_FLAGS is a plain object literal: no
// spread, no Object.fromEntries, no computed keys, so its runtime key set is
// provably `keyof typeof FEATURE_FLAGS`. Build the registry by merging two
// objects and that stops being true, silently — keep it a literal.
export const FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS);
export function flagDefault(key) {
    return FEATURE_FLAGS[key].default;
}
export function isFeatureFlagKey(value) {
    // hasOwnProperty rather than `value in FEATURE_FLAGS`: the latter accepts
    // "toString" and every other name on Object.prototype.
    return Object.prototype.hasOwnProperty.call(FEATURE_FLAGS, value);
}
export function toFlagUserAttributes(user) {
    if (!user) {
        return undefined;
    }
    const custom = { tenantId: user.tenantId };
    // Blank optionals are OMITTED, never sent as "". To ConfigCat an empty string
    // is a value like any other, so a rule such as `tenantName IS NOT ""` would
    // match a user who simply has no tenant name.
    if (user.tenantName && user.tenantName.trim().length > 0) {
        custom.tenantName = user.tenantName;
    }
    if (user.environment && user.environment.trim().length > 0) {
        custom.environment = user.environment;
    }
    if (user.tenantCountry && user.tenantCountry.trim().length > 0) {
        custom.tenantCountry = user.tenantCountry;
    }
    if (user.language && user.language.trim().length > 0) {
        custom.language = user.language;
    }
    const attributes = { identifier: user.userId, custom };
    if (user.email && user.email.trim().length > 0) {
        attributes.email = user.email;
    }
    return attributes;
}
